import { Injectable } from '@angular/core';
import { State, Selector, Action, StateContext } from '@ngxs/store';
import { Observable, throwError, of } from 'rxjs';
import { tap, catchError } from 'rxjs/operators';
import {
  CACHE_CONFIG,
  CacheKeys,
  PulseStateModel,
} from 'ng2-app/models/pulse/pulse-state.model';
import { PulseService } from 'ng2-app/pulse/pulse.service';
import { ScoringGroup } from 'ng2-app/models/pulse/scoring-group.model';
import {
  LenderUsersWithScoringGroups,
} from 'ng2-app/models/pulse/scores.model';
import {
  GetScoringGroupsByLenderUserId,
  GetAllScoringGroups,
  GetTeamsWithScoringGroups,
  GetLenderUsersWithScoringGroups
} from 'ng2-app/store/pulse/pulse.action';
import { Team } from 'app/models/dto/lender-user.model';

@State<PulseStateModel>({
  name: 'pulse',
  defaults: {
    primaryScoringGroups: [],
    secondaryScoringGroups: [],
    teamsWithScoringGroups: [],
    lenderUsersWithScoringGroups: {},
    loading: false,
    fetching: false,
    lastSync: {
      primaryScoringGroups: null,
      secondaryScoringGroups: null,
      teamsWithScoringGroups: null,
      lenderUsersWithScoringGroups: null,
    }
  }
})

@Injectable()

export class PulseState {
  @Selector()
  static loading(state: PulseStateModel) {
    return state?.loading ?? null;
  }

  @Selector()
  static fetching(state: PulseStateModel) {
    return state?.fetching ?? null;
  }

  @Selector()
  static primaryScoringGroups(state: PulseStateModel) {
    return state?.primaryScoringGroups ?? null;
  }

  @Selector()
  static primaryScoringGroupsLastSync(state: PulseStateModel) {
    return state?.lastSync?.primaryScoringGroups;
  }
  @Selector()
  static secondaryScoringGroups(state: PulseStateModel) {
    return state?.secondaryScoringGroups ?? null;
  }

  @Selector()
  static secondaryScoringGroupsLastSync(state: PulseStateModel) {
    return state?.lastSync?.secondaryScoringGroups;
  }

  @Selector()
  static scoringGroups(state: PulseStateModel) {
    const allGroups = [...state?.primaryScoringGroups, ...state?.secondaryScoringGroups];
    const uniqueGroups = new Set<number>();

    return allGroups.filter(group => {
      if (uniqueGroups.has(group.id)) {
        return false;
      }
      uniqueGroups.add(group.id);
      return true;
    });
  }

  @Selector()
  static teamsWithScoringGroups(state: PulseStateModel) {
    return state?.teamsWithScoringGroups ?? null;
  }
  @Selector()
  static teamsWithScoringGroupsLastSync(state: PulseStateModel) {
    return state?.lastSync?.teamsWithScoringGroups;
  }

  @Selector()
  static lenderUsersWithScoringGroups(state: PulseStateModel) {
    return state?.lenderUsersWithScoringGroups ?? null;
  }

  @Selector()
  static lastSync(state: PulseStateModel) {
    return state?.lastSync ?? null;
  }

  constructor(
    private _pulseService: PulseService
  ) {}

  private isCacheValid(
    ctx: StateContext<PulseStateModel>,
    cacheKey: CacheKeys,
    force: boolean = false
  ): boolean {
    if (force) {
      return false;
    }

    const config = CACHE_CONFIG[cacheKey];
    if (!config || config.alwaysSync) {
      return false;
    }

    const cache = ctx.getState().lastSync[cacheKey];
    if (!cache || !(cache instanceof Date)) {
      return false;
    }

    return cache.getTime() + config.ttl > Date.now();
  }

  private updateLastSync(ctx: StateContext<PulseStateModel>, cacheKey: CacheKeys) {
    ctx.patchState({
      lastSync: {
        ...ctx.getState().lastSync,
        [cacheKey]: new Date()
      }
    });
  }

  @Action(GetScoringGroupsByLenderUserId)
  getScoreGroupsByLenderUserId(
    ctx: StateContext<PulseStateModel>,
    {
      lenderUserId,
      patch = 'primaryScoringGroups',
      overrideCache = false
    }: GetScoringGroupsByLenderUserId
  ): Observable<ScoringGroup[]> {
    const cacheKey = patch as CacheKeys;
    const isCacheValid = this.isCacheValid(ctx, cacheKey, overrideCache);
    if (isCacheValid && !overrideCache) {
      return of(ctx.getState()[patch]);
    }

    const currentData = ctx.getState()[patch];
    const hasData = currentData?.length > 0;

    ctx.patchState({
      fetching: true,
      loading: !hasData
    });

    return this._pulseService.getScoringGroupsByLenderUserId(lenderUserId).pipe(
      tap(scoringGroups => {
        ctx.patchState({
          [patch]: scoringGroups,
          loading: false,
          fetching: false
        });
        this.updateLastSync(ctx, cacheKey);
      }),
      catchError(error => {
        ctx.patchState({ loading: false, fetching: false });
        return throwError(() => error);
      })
    );
  }

  @Action(GetAllScoringGroups)
  getAllScoringGroups(
    ctx: StateContext<PulseStateModel>,
    { overrideCache = false }: GetAllScoringGroups
  ): Observable<ScoringGroup[]> {
    const isCacheValid = this.isCacheValid(ctx, 'primaryScoringGroups', overrideCache);
    if (isCacheValid && !overrideCache) {
      return of(ctx.getState().primaryScoringGroups);
    }

    const currentData = ctx.getState().primaryScoringGroups;
    const hasData = currentData?.length > 0;

    ctx.patchState({
      fetching: true,
      loading: !hasData
    });

    return this._pulseService.getAllScoringGroups().pipe(
      tap(scoringGroups => {
        ctx.patchState({ primaryScoringGroups: scoringGroups, loading: false, fetching: false });
        this.updateLastSync(ctx, 'primaryScoringGroups');
      }),
      catchError(error => {
        ctx.patchState({ loading: false, fetching: false });
        return throwError(() => error);
      })
    );
  }

  @Action(GetTeamsWithScoringGroups)
  getTeamsWithScoringGroups(
    ctx: StateContext<PulseStateModel>,
    { includeZeroMemberTeams = false, overrideCache = false}: GetTeamsWithScoringGroups
  ): Observable<Team[]> {

    const isCacheValid = this.isCacheValid(ctx, 'teamsWithScoringGroups', overrideCache);
    if (isCacheValid && !overrideCache) {
      return of(ctx.getState().teamsWithScoringGroups);
    }

    const currentTeamsWithScoringGroups = ctx.getState().teamsWithScoringGroups;
    const hasData = currentTeamsWithScoringGroups?.length > 0;

    ctx.patchState({
      fetching: true,
      loading: !hasData
    });

    return this._pulseService.getTeamsWithScoringGroups(includeZeroMemberTeams).pipe(
      tap(teams => {
        teams.sort((a, b) => a.name.localeCompare(b.name, 'en', { sensitivity: 'base' }));
        ctx.patchState({ teamsWithScoringGroups: teams, loading: false, fetching: false });
        this.updateLastSync(ctx, 'teamsWithScoringGroups');
      }),
      catchError(error => {
        ctx.patchState({ loading: false, fetching: false });
        return throwError(() => error);
      })
    );
  }

  @Action(GetLenderUsersWithScoringGroups)
  getLenderUsersWithScoringGroups(
    ctx: StateContext<PulseStateModel>,
    { overrideCache = false}: GetLenderUsersWithScoringGroups
  ): Observable<LenderUsersWithScoringGroups> {
    const isCacheValid = this.isCacheValid(ctx, 'lenderUsersWithScoringGroups', overrideCache);
    if (isCacheValid && !overrideCache) {
      return of(ctx.getState().lenderUsersWithScoringGroups);
    }

    const currentLenderUsersWithScoringGroups = ctx.getState().lenderUsersWithScoringGroups;
    const hasData = Object.keys(currentLenderUsersWithScoringGroups).length > 0;
    ctx.patchState({
      fetching: true,
      loading: !hasData
    });

    return this._pulseService.getLenderUsersWithScoringGroups().pipe(
      tap(lenderUsers => {
        ctx.patchState({ lenderUsersWithScoringGroups: lenderUsers, loading: false, fetching: false });
        this.updateLastSync(ctx, 'lenderUsersWithScoringGroups');
      }),
      catchError(error => {
        ctx.patchState({ loading: false, fetching: false });
        return throwError(() => error);
      })
    );
  }
}
